/**
 * AppStateStore manages the application's navigation state, specifically which module
 * and tasks are currently selected. This is the single source of truth for selection
 * state across the app, replacing route params for a more predictable state management.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '../auth/store';
import type { User } from '../api/models';
import type { UUID } from '../types/ids';
import { isUUID } from '../types/ids';

/**
 * Represents a selected module with its course context.
 * Null when no module is selected.
 *
 * IDs are typed as UUID to enforce validation at boundaries.
 * This prevents "undefined" or invalid strings from entering the system.
 */
export type ModuleSelection = {
  moduleId: UUID;
  courseId: UUID;
} | null;

/**
 * Core application state interface defining the selection state and its mutations.
 */
export interface AppState {
  /** Currently selected module and its course context */
  selectedModule: ModuleSelection;

  /** Map of moduleId to selected taskId. Null taskId means no task selected for that module */
  selectedTaskIds: Record<UUID, UUID | null>;

  // Computed selectors
  /** Returns true if a module is currently selected with valid IDs */
  isModuleSelected: () => boolean;

  /** Returns true if a task is currently selected for the current module */
  isTaskSelected: () => boolean;

  // Actions
  /** Set the currently selected module and its course context */
  setSelectedModule: (selection: ModuleSelection) => void;

  /** Set the selected task for a specific module */
  setSelectedTask: (moduleId: UUID, taskId: UUID | null) => void;

  /** Clear all selections (used when logging out or switching context) */
  clearSelection: () => void;

  restoreUserContext: (user: User) => void;
}

const store = create<AppState>((set, get) => ({
  selectedModule: null,
  selectedTaskIds: {},

  // Computed selectors
  isModuleSelected: () => {
    const { selectedModule } = get();
    return selectedModule !== null;
  },

  isTaskSelected: () => {
    const { selectedModule, selectedTaskIds } = get();
    if (!selectedModule) return false;
    const taskId = selectedTaskIds[selectedModule.moduleId];
    return taskId !== null && taskId !== undefined;
  },

  setSelectedModule: (selection) => set({ selectedModule: selection }),

  setSelectedTask: (moduleId, taskId) => set((state) => ({
    selectedTaskIds: {
      ...state.selectedTaskIds,
      [moduleId]: taskId,
    },
  })),

  clearSelection: () => set({
    selectedModule: null,
    selectedTaskIds: {},
  }),

  restoreUserContext: (user) => {
    if (user.role === 'student' && user.context?.currentModule) {
      const { moduleId, courseId, currentTaskId } = user.context.currentModule;

      // Validate IDs at boundary (data from API)
      if (isUUID(moduleId) && isUUID(courseId)) {
        set((state) => ({
          selectedModule: { moduleId, courseId },
          selectedTaskIds: currentTaskId && isUUID(currentTaskId)
            ? { ...state.selectedTaskIds, [moduleId]: currentTaskId }
            : state.selectedTaskIds,
        }));
      } else {
        // Invalid IDs from API - log but don't crash
        // eslint-disable-next-line no-console
        console.warn('[AppStateStore] Invalid IDs in user context', { moduleId, courseId });
      }
    }
  },
}));

/**
 * Type-safe hook for accessing module selection state.
 * Use this when you need to know/change which module is currently selected.
 * @returns Module selection state and actions
 */
export const useModuleSelectionState = () => store(
  useShallow((state) => ({
    selectedModule: state.selectedModule,
    setSelectedModule: state.setSelectedModule,
  })),
);

/**
 * Type-safe hook for accessing task selection state for a specific module.
 * Use this when you need to know/change which task is currently selected.
 * @param moduleId - The ID of the module to get task selection for
 * @returns Task selection state and actions
 */
export const useTaskSelectionState = (moduleId: UUID | undefined) => store(
  useShallow((state) => ({
    taskId: moduleId ? state.selectedTaskIds[moduleId] : null,
    setSelectedTask: state.setSelectedTask,
  })),
);

/**
 * Type-safe hook for accessing both module and task selection state.
 * Use this when you need to manage both module and task selection together.
 * @returns Combined module and task selection state and actions
 */
export const useModuleAndTaskSelectionState = () => store(
  useShallow((state) => ({
    selectedModule: state.selectedModule,
    taskId: state.selectedModule?.moduleId ? state.selectedTaskIds[state.selectedModule.moduleId] : null,
    isModuleSelected: state.isModuleSelected(),
    isTaskSelected: state.isTaskSelected(),
    setSelectedModule: state.setSelectedModule,
    setSelectedTask: state.setSelectedTask,
    clearSelection: state.clearSelection,
  })),
);

/**
 * Hook for checking selection state without subscribing to the actual values.
 * Use this when you only need to know IF something is selected, not what it is.
 * This prevents unnecessary re-renders when the IDs change but selection state doesn't.
 *
 * @example
 * ```typescript
 * const { isModuleSelected, isTaskSelected } = useSelectionChecks();
 *
 * if (!isModuleSelected) {
 *   return <SelectModulePrompt />;
 * }
 * ```
 */
export const useSelectionChecks = () => store(
  useShallow((state) => ({
    isModuleSelected: state.isModuleSelected(),
    isTaskSelected: state.isTaskSelected(),
  })),
);

export const useAppStateStore = store;

// Watch auth store for user changes
useAuthStore.subscribe((state) => {
  const { user } = state;
  if (user) {
    // Only restore user context if there is no active selection.
    // This prevents stale context from the user profile (fetched on token refresh)
    // from overwriting the student's current work.
    const currentSelection = useAppStateStore.getState().selectedModule;
    if (!currentSelection?.moduleId) {
      useAppStateStore.getState().restoreUserContext(user);
    }
  } else {
    useAppStateStore.getState().clearSelection();
  }
});

export default store;
